import type { Analytics } from 'firebase/analytics';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

type LogEventFn = (analytics: Analytics, eventName: string, eventParams?: Record<string, string | number | boolean>) => void;

let _analytics: Analytics | null = null;
let _logEvent: LogEventFn | null = null;
let _appVersion = '1.0.0';
let _appBuild = '1';
let _isNative = false;
let _platform = 'web';

// Initialize platform info
if (Capacitor.isNativePlatform()) {
  _isNative = true;
  _platform = Capacitor.getPlatform();
  App.getInfo().then((info) => {
    if (info) {
      _appVersion = info.version || '1.0.0';
      _appBuild = info.build || '1';
      syncUserProperties();
    }
  }).catch(() => {});
}

function syncUserProperties() {
  if (!_analytics) return;
  import('firebase/analytics').then(({ setUserProperties }) => {
    if (_analytics) {
      setUserProperties(_analytics, {
        platform_type: _isNative ? `${_platform}_native` : 'web_pwa',
        app_version: _appVersion,
        app_build: _appBuild,
        device_platform: _platform,
      });
    }
  }).catch(() => {});
}

export function setAnalytics(a: Analytics, logFn?: LogEventFn) {
  _analytics = a;
  if (logFn) {
    _logEvent = logFn;
  }
  syncUserProperties();
  logAppOpen();
}

function log(event: string, params?: Record<string, string | number | boolean>) {
  if (!_analytics) return;
  const enrichedParams = {
    platform: _platform,
    is_native: _isNative,
    app_version: _appVersion,
    ...params,
  };

  if (_logEvent) {
    _logEvent(_analytics, event, enrichedParams);
  } else {
    import('firebase/analytics').then(({ logEvent }) => {
      _logEvent = logEvent as unknown as LogEventFn;
      if (_analytics) {
        logEvent(_analytics, event, enrichedParams);
      }
    });
  }
}

export function logAppOpen() {
  log('app_open', {
    platform: _platform,
    app_version: _appVersion,
    app_build: _appBuild,
  });
}

export function logShareCase(caseNumber: string, location: string, source: 'list' | 'sheet' | 'native_share') {
  log('share_case', { case_number: caseNumber, location, source });
}

export function logDeepLinkOpen(caseNumber: string, location: string) {
  log('open_deep_link', { case_number: caseNumber, location });
}

export function logSearch(queryLength: number, resultCount: number) {
  log('search_cause_list', { query_length: queryLength, result_count: resultCount });
}

export function logTabSwitch(tab: 'live_board' | 'cause_list' | 'daily_orders') {
  log('switch_tab', { tab });
}

export function logLocationSwitch(location: string) {
  log('switch_location', { location });
}

export function logOrderPdfView(caseNumber: string, date: string) {
  log('view_order_pdf', { case_number: caseNumber, date });
}

export function logInAppReview(action: 'prompted' | 'completed' | 'failed') {
  log('in_app_review', { action });
}

export function logInAppUpdate(action: 'available' | 'started' | 'downloaded' | 'completed' | 'failed', version?: string) {
  log('in_app_update', { action, update_version: version ?? 'unknown' });
}

export function logAppError(message: string, stack?: string, fatal = false) {
  log('app_error', {
    error_message: message.slice(0, 100),
    error_stack: stack ? stack.slice(0, 200) : '',
    fatal,
  });
}

export type PromoPlacement = 'dialog' | 'header_pill' | 'footer_link' | 'top_banner' | 'qr_code';

export function logAppPromoShown(trigger: 'timer' | 'engagement' | 'manual_click') {
  log('app_promo_shown', { trigger });
}

export function logAppPromoClicked(source: PromoPlacement) {
  log('app_promo_clicked', { source, destination: 'play_store' });
}

export function logAppPromoDismissed(action: 'close_btn' | 'maybe_later' | 'backdrop') {
  log('app_promo_dismissed', { action });
}

